import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router';
import type { IconType } from 'react-icons';
import {
    MdOutlineDashboard,
    MdOutlineGroup,
    MdOutlineFactCheck,
    MdOutlineCalendarMonth,
    MdOutlineHistory,
    MdOutlineSettings,
    MdOutlinePeopleAlt,
    MdOutlineMenu,
    MdClose,
    MdOutlineLogout,
} from 'react-icons/md';
import { PRODUCT_URLS } from '@podus/utils';
import { useAuth } from '@/context/AuthContext';

export interface NavItem {
    label: string;
    icon: IconType;
    to: string;
    end?: boolean;
}

export const AMBASSADOR_NAV: NavItem[] = [
    { label: 'Dashboard', icon: MdOutlineDashboard, to: '/dashboard', end: true },
    { label: 'My referrals', icon: MdOutlinePeopleAlt, to: '/dashboard/referrals' },
    { label: 'Settings', icon: MdOutlineSettings, to: '/dashboard/settings' },
];

export const ADMIN_NAV: NavItem[] = [
    { label: 'Overview', icon: MdOutlineDashboard, to: '/admin', end: true },
    // Invitations live inside the ambassador roster — an invite is just an
    // ambassador row that hasn't been accepted yet.
    { label: 'Ambassadors', icon: MdOutlineGroup, to: '/admin/ambassadors' },
    { label: 'Daily breakdown', icon: MdOutlineCalendarMonth, to: '/admin/daily' },
    { label: 'Attribution review', icon: MdOutlineFactCheck, to: '/admin/referrals' },
    { label: 'Audit log', icon: MdOutlineHistory, to: '/admin/audit' },
];

export interface DashboardLayoutProps {
    navItems: NavItem[];
    title: string;
}

/** Shell shared by the ambassador and admin dashboards. */
export default function DashboardLayout({ navItems, title }: DashboardLayoutProps) {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/', { replace: true });
    };

    const initial = (user?.name ?? user?.email ?? 'A').charAt(0).toUpperCase();

    return (
        <div className="flex h-dvh bg-white">
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-30 bg-black/30 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                    aria-hidden="true"
                />
            )}

            <aside
                className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-[#E0E0E0] bg-white transition-transform lg:static lg:translate-x-0 ${
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                }`}
            >
                <div className="flex h-16 items-center gap-2 border-b border-[#E0E0E0] px-5">
                    <img src="/logo.svg" alt="Podus" className="h-7 w-auto" />
                    <span className="text-[15px] font-bold text-secondary">Ambassador</span>
                </div>

                <nav className="flex-1 overflow-y-auto px-3 py-4">
                    {navItems.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            onClick={() => setSidebarOpen(false)}
                            className={({ isActive }) =>
                                `mb-1.5 flex items-center gap-3 rounded-lg px-3 py-2.5 text-[14px] leading-6 transition-colors ${
                                    isActive
                                        ? 'bg-[#f5f5f5] font-bold text-secondary'
                                        : 'text-[#4E4D4D] hover:bg-[#f8f8f8]'
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    <item.icon size={20} className={isActive ? 'text-primary' : ''} />
                                    {item.label}
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="border-t border-[#E0E0E0] p-3">
                    <a
                        href={PRODUCT_URLS.podusai}
                        className="block rounded-lg px-3 py-2 text-[13px] text-[#747474] transition-colors hover:bg-[#f8f8f8]"
                    >
                        Visit Podus AI ↗
                    </a>
                </div>
            </aside>

            <div className="flex flex-1 flex-col overflow-hidden">
                <header className="flex h-16 shrink-0 items-center gap-3 border-b border-[#E0E0E0] px-4 lg:px-8">
                    <button type="button" title="Open menu" onClick={() => setSidebarOpen(true)} className="lg:hidden">
                        <MdOutlineMenu size={24} className="text-secondary" />
                    </button>

                    <h1 className="truncate text-[16px] font-bold text-secondary">{title}</h1>

                    <div className="flex-1" />

                    <div className="hidden text-right sm:block">
                        <p className="text-[13px] font-semibold leading-4 text-secondary">{user?.name ?? 'Account'}</p>
                        <p className="text-[11px] leading-4 text-[#9a9a9a]">{user?.email}</p>
                    </div>

                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-xs font-bold text-white">
                        {initial}
                    </span>

                    <button
                        type="button"
                        title="Sign out"
                        onClick={handleLogout}
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-[#555] transition-colors hover:bg-[#f5f5f5]"
                    >
                        <MdOutlineLogout size={20} />
                    </button>
                </header>

                <main className="flex-1 overflow-y-auto bg-[#fbfbfb]">
                    <Outlet />
                </main>
            </div>

            {sidebarOpen && (
                <button
                    type="button"
                    title="Close menu"
                    onClick={() => setSidebarOpen(false)}
                    className="fixed left-64 top-4 z-50 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-md lg:hidden"
                >
                    <MdClose size={18} />
                </button>
            )}
        </div>
    );
}
